export type ApiOkResponse<T = Record<string, any>> = {
  success: true;
} & T;

export interface ApiErrorResponse {
  success: false;
  error: {
    message: string;
    code?: string;
  };
}

export type ApiResponse<T = Record<string, any>> =
  | ApiOkResponse<T>
  | ApiErrorResponse;

export type MappedError =
  | { type: 'field'; fieldErrors: Record<string, string> }
  | { type: 'form'; message: string };

export type ErrorMapper = (error: any) => MappedError;

export const defaultErrorMapper: ErrorMapper = (err: any): MappedError => {
  const msg = err?.api?.error?.message ?? err?.message ?? '';

  if (msg.includes('BrokenCircuitException')) {
    return {
      type: 'form',
      message:
        'We are temporarily experiencing technical difficulties. Please try again shortly.',
    };
  }

  return {
    type: 'form',
    message: 'Something went wrong. Please try again.',
  };
};

export function extractErrorMessage(error: any): string {
  return error?.api?.error?.message ?? error?.message ?? 'Unknown error';
}

export function errorContains(error: any, text: string): boolean {
  const msg = extractErrorMessage(error);
  return msg.includes(text);
}

export function createFieldError(field: string, message: string): MappedError {
  return {
    type: 'field',
    fieldErrors: { [field]: message },
  };
}

export function createFormError(message: string): MappedError {
  return {
    type: 'form',
    message,
  };
}

export async function readBodySafe(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export function throwApiError(res: Response, body: unknown): never {
  const apiError: ApiErrorResponse =
    body && typeof body === 'object'
      ? (body as ApiErrorResponse)
      : {
          success: false,
          error: {
            message: String(body ?? res.statusText ?? 'Request failed'),
          },
        };

  throw Object.assign(new Error(apiError?.error?.message ?? 'Request failed'), {
    status: res.status,
    api: apiError,
  });
}

export function isApiSuccess<T>(
  response: ApiResponse<T> | null
): response is ApiOkResponse<T> {
  return response !== null && response.success === true;
}

export function isApiError<T>(
  response: ApiResponse<T> | null
): response is ApiErrorResponse {
  return response !== null && response.success === false;
}

export function handleApiResponse<T, TSuccess, TError>(
  response: ApiResponse<T> | null,
  handlers: {
    onSuccess: (data: ApiOkResponse<T>) => TSuccess;
    onError: (error: ApiErrorResponse) => TError;
    onNull?: () => TError;
  }
): TSuccess | TError {
  if (response === null) {
    return handlers.onNull
      ? handlers.onNull()
      : handlers.onError({
          success: false,
          error: { message: 'No response received' },
        });
  }

  if (isApiSuccess(response)) {
    return handlers.onSuccess(response);
  }

  return handlers.onError(response);
}

export function handleMappedError(
  mappedError: MappedError,
  handlers: {
    onFieldError: (fieldErrors: Record<string, string>) => void;
    onFormError: (message: string) => void;
  }
): void {
  if (mappedError.type === 'field') {
    handlers.onFieldError(mappedError.fieldErrors);
  } else {
    handlers.onFormError(mappedError.message);
  }
}

export function isFieldError(
  error: MappedError
): error is { type: 'field'; fieldErrors: Record<string, string> } {
  return error.type === 'field';
}

export function isFormError(
  error: MappedError
): error is { type: 'form'; message: string } {
  return error.type === 'form';
}

export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';
